import * as React from 'react'
import { useCallback, useState } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { IUserNotificationsSettings } from '@user/store/users/typings'
import { Field, Form, useFormikContext } from 'formik'
import { MuiTextFieldFormik, SideDrawerForm } from '@sales-center/components'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { AdditionalEmployees } from '@user/components/Settings/Notifications/forms/ManageSlideout/AdditionalEmployees'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Switch from '@mui/material/Switch'

interface Props {
  onClose: () => void
}

export function NotificationSettingFormFields(props: Props) {
  const { onClose } = props

  const { setFieldValue, values, submitForm, isSubmitting } =
    useFormikContext<IUserNotificationsSettings>()

  const [customEmailsDisabled, setCustomEmailsDisabled] = useState(
    values.useDefaultNotificationMailState,
  )

  const onChangeMonthlySummery = useCallback(
    (_e, checked: boolean) => {
      setFieldValue('subscribeToMonthlySummaryEmails', checked)
    },
    [setFieldValue],
  )

  const onNotificationTypeChangeHandler = useCallback(
    (_e, value: string) => {
      setFieldValue('receiveAllNotificationsState', value === 'all')
    },
    [setFieldValue],
  )

  const onEmailTypeChangeHandler = useCallback(
    (_e, value: string) => {
      const isUser = value === 'user'
      setFieldValue('useDefaultNotificationMailState', isUser)

      if (isUser) {
        setCustomEmailsDisabled(true)
        setFieldValue('additional', [])
      } else {
        setCustomEmailsDisabled(false)
      }
    },
    [setFieldValue, setCustomEmailsDisabled],
  )

  const onSave = () => {
    submitForm()
  }

  const currentUser = useSelector(selectCurrentUser)

  return (
    <SideDrawerForm
      onSave={onSave}
      primaryBtnLabel={'Save'}
      disableSave={isSubmitting}
      onCancel={onClose}
    >
      <Form>
        <div>
          <Box ml={2} mt={4}>
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle2">Notification Delivery</Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Typography variant="body2">
                Designate an email address where notifications are sent
              </Typography>
            </Box>
          </Box>
          <RadioGroup
            onChange={onEmailTypeChangeHandler}
            defaultValue={
              values.useDefaultNotificationMailState ? 'user' : 'custom'
            }
          >
            <Box ml={2.5} mt={3}>
              <FormControlLabel
                control={<Radio />}
                value={'user'}
                label={'System Notification Email'}
              />
              <Box ml={2} width="470px">
                <Field
                  name="userEmail"
                  label=""
                  component={MuiTextFieldFormik}
                  type="string"
                  fullWidth
                  size="small"
                  required={false}
                  disabled={true}
                  value={currentUser.emailAddress}
                />
              </Box>
            </Box>
            <Box ml={2.5} mt={2}>
              <FormControlLabel
                control={<Radio />}
                value={'custom'}
                label={'Custom Email(s)'}
              />
              <Box ml={2} width="470px">
                <AdditionalEmployees
                  options={[]}
                  placeHolder={'Add Additional Email'}
                  maxItems={3}
                  disabled={customEmailsDisabled}
                  required={!customEmailsDisabled}
                />
              </Box>
            </Box>
          </RadioGroup>
          <Box ml={2} mt={3}>
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle2">
                Notification Preferences
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Typography variant="body2">
                Your administrator configures the types of notifications you
                receive. Choose below which records you want to be included.
              </Typography>
            </Box>
          </Box>
          <RadioGroup
            onChange={onNotificationTypeChangeHandler}
            defaultValue={
              values.receiveAllNotificationsState ? 'all' : 'onlyMine'
            }
          >
            <Box ml={2.5} mt={3}>
              <FormControlLabel
                control={<Radio />}
                value={'onlyMine'}
                label={'Only My Records (Less Notifications)'}
              />
              <Typography variant="body2" ml={3}>
                You will receive notifications for only records assigned to you.
                Your assigned Sales Team(s) and position(s) are still respected
                and you will only receive notifications that your Administrator
                has configured for you.
              </Typography>
            </Box>
            <Box ml={2.5} mt={2}>
              <FormControlLabel
                control={<Radio />}
                value={'all'}
                label={'All Records (More Notifications)'}
              />
              <Typography variant="body2" ml={3}>
                You will receive notifications for records owned by you and also
                records owned by others. Your assigned Sales Team(s) and
                position(s) are still respected and you will only receive
                notifications that your Administrator has configured for you.
              </Typography>
            </Box>
          </RadioGroup>
          <Box ml={2} mt={3}>
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle2">
                Monthly Summary Emails
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Typography variant="body2">
                Get personalized aggregate sales funnel summary statistics
                delivered to your inbox each month. View key metrics like
                average time to close, closed/won conversion rate, total dollars
                closed, and more without logging into Sales Center.
              </Typography>
            </Box>

            <Box display="flex" mt={2} mb={4}>
              <Switch
                size="small"
                checked={values.subscribeToMonthlySummaryEmails}
                onChange={onChangeMonthlySummery}
                onClick={(evt) => evt.stopPropagation()}
              />
              <Typography variant="body2">
                Subscribe to Monthly Summary Emails
              </Typography>
            </Box>
          </Box>
        </div>
      </Form>
    </SideDrawerForm>
  )
}
